import posthog from "posthog-js";

const NOTRACK_KEY = "pick3_notrack";

/**
 * Check if current user has opted out of tracking.
 * Useful for preserving notrack param in generated URLs.
 */
export const isNoTrackEnabled = (): boolean => {
  return localStorage.getItem(NOTRACK_KEY) === "1";
};

/**
 * Append notrack param to a URL if user has opted out.
 * Use this when generating share URLs to preserve opt-out state.
 */
export const preserveNoTrack = (url: string): string => {
  if (!isNoTrackEnabled()) return url;

  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}notrack=1`;
};

/**
 * Analytics event names for PostHog tracking.
 * Centralized here for consistency and type safety.
 */
export const AnalyticsEvents = {
  PAGE_VIEWED: "page_viewed",
  RANK_SLOT_OPENED: "rank_slot_opened",
  MODEL_SELECTED: "model_selected",
  BLURB_ENTERED: "blurb_entered",
  HANDLE_ENTERED: "handle_entered",
  TITLE_ENTERED: "title_entered",
  COPY_LINK_CLICKED: "copy_link_clicked",
  COPY_LINK_CREATED: "copy_link_created",
  COPY_LINK_RECOPIED: "copy_link_recopied",
  DOWNLOAD_CLICKED: "download_clicked",
  START_OVER_CLICKED: "start_over_clicked",
  REMIX_CLICKED: "remix_clicked",
  RANKING_COMPLETED: "ranking_completed",
  RANKING_AND_BLURBS_COMPLETED: "ranking_and_blurbs_completed",
  FEEDBACK_SUBMITTED: "feedback_submitted",
  RANKINGS_PAGE_CLICKED: "rankings_page_clicked",
  RANKING_FEEDBACK_SUBMITTED: "ranking_feedback_submitted",
} as const;

export type AnalyticsEvent = (typeof AnalyticsEvents)[keyof typeof AnalyticsEvents];

/**
 * Determine the current stage based on hostname.
 */
const getStage = (): "local" | "prod" => {
  if (typeof window === "undefined") return "prod";
  const hostname = window.location.hostname;
  return hostname === "localhost" || hostname === "127.0.0.1" ? "local" : "prod";
};

/**
 * Capture referrer at session start (module initialization).
 * Stored once and included in all subsequent events.
 */
const sessionReferrer =
  typeof document !== "undefined" && document.referrer ? document.referrer : null;

/**
 * Capture wrapper that automatically includes stage and referrer in all events.
 */
const capture = (event: string, properties?: Record<string, unknown>) => {
  posthog.capture(event, {
    ...properties,
    stage: getStage(),
    referrer: sessionReferrer,
  });
};

/**
 * Lightweight wrapper for PostHog analytics.
 * All tracking goes through this module for consistency.
 */
export const analytics = {
  /**
   * Track a custom event with optional properties.
   */
  track: (event: AnalyticsEvent, properties?: Record<string, unknown>) => {
    capture(event, properties);
  },

  /**
   * Track page view
   */
  pageView: (pageName?: string) => {
    capture(AnalyticsEvents.PAGE_VIEWED, {
      page: pageName ?? window.location.pathname,
    });
  },

  /**
   * Track when user opens a rank slot to select a model
   */
  rankSlotOpened: (rank: 1 | 2 | 3) => {
    capture(AnalyticsEvents.RANK_SLOT_OPENED, { rank });
  },

  /**
   * Track when user selects a model for a rank
   */
  modelSelected: (rank: 1 | 2 | 3, model: string) => {
    capture(AnalyticsEvents.MODEL_SELECTED, { rank, model });
  },

  /**
   * Track when user enters a blurb (fired on blur when there's text)
   */
  blurbEntered: (rank: 1 | 2 | 3, blurbLength: number) => {
    capture(AnalyticsEvents.BLURB_ENTERED, { rank, blurb_length: blurbLength });
  },

  /**
   * Track when user enters a handle (fired on blur when there's text)
   */
  handleEntered: (handle: string) => {
    capture(AnalyticsEvents.HANDLE_ENTERED, { handle_length: handle.length });
  },

  /**
   * Track when user enters a card title (fired on blur when there's text)
   */
  titleEntered: (titleLength: number) => {
    capture(AnalyticsEvents.TITLE_ENTERED, { title_length: titleLength });
  },

  /**
   * Track when user clicks download
   */
  downloadClicked: (isComplete: boolean) => {
    capture(AnalyticsEvents.DOWNLOAD_CLICKED, { is_complete: isComplete });
  },

  /**
   * Track when user clicks start over
   */
  startOverClicked: (modelsSelected: number) => {
    capture(AnalyticsEvents.START_OVER_CLICKED, { models_selected: modelsSelected });
  },

  /**
   * Track when user clicks copy link button (before creation)
   */
  copyLinkClicked: () => {
    capture(AnalyticsEvents.COPY_LINK_CLICKED);
  },

  /**
   * Track when link is successfully created and copied
   */
  copyLinkCreated: (shortCode: string, clipboardSuccess: boolean) => {
    capture(AnalyticsEvents.COPY_LINK_CREATED, {
      short_code: shortCode,
      clipboard_success: clipboardSuccess,
    });
  },

  /**
   * Track when user re-copies an existing link
   */
  copyLinkRecopied: (clipboardSuccess: boolean) => {
    capture(AnalyticsEvents.COPY_LINK_RECOPIED, { clipboard_success: clipboardSuccess });
  },

  /**
   * Track when user clicks remix on a shared card
   */
  remixClicked: (shortCode: string) => {
    capture(AnalyticsEvents.REMIX_CLICKED, { short_code: shortCode });
  },

  /**
   * Track when all 3 ranks have models selected (blurbs optional)
   */
  rankingCompleted: (models: [string, string, string]) => {
    capture(AnalyticsEvents.RANKING_COMPLETED, {
      model_1: models[0],
      model_2: models[1],
      model_3: models[2],
    });
  },

  /**
   * Track when all 3 ranks have models AND blurbs filled out
   */
  rankingAndBlurbsCompleted: (models: [string, string, string]) => {
    capture(AnalyticsEvents.RANKING_AND_BLURBS_COMPLETED, {
      model_1: models[0],
      model_2: models[1],
      model_3: models[2],
    });
  },

  /**
   * Track when user submits feedback
   */
  feedbackSubmitted: (messageLength: number) => {
    capture(AnalyticsEvents.FEEDBACK_SUBMITTED, { message_length: messageLength });
  },

  /**
   * Track when user clicks "See Model Rankings" link
   */
  rankingsPageClicked: () => {
    capture(AnalyticsEvents.RANKINGS_PAGE_CLICKED);
  },

  /**
   * Track when user submits ranking feedback
   */
  rankingFeedbackSubmitted: (messageLength: number) => {
    capture(AnalyticsEvents.RANKING_FEEDBACK_SUBMITTED, { message_length: messageLength });
  },
};

